import React, { useState } from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Typography,
    Divider,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    styled
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Assignment as FormsIcon,
    School as ApplicationIcon,
    Settings as ProgrammeIcon,
    People as UserManagementIcon,
    ExpandLess,
    ExpandMore,
    AccountCircle,
    Logout,
    Group as GroupIcon,
    PersonAdd as PersonAddIcon,
    Menu as MenuIcon,
    ChevronLeft,
    ChevronRight,
    Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../users/AuthContext';
import { UnikLog } from '../assets';

const drawerWidth = 280;
const collapsedWidth = 80;

const Sidebar = ({ mobileOpen, onMobileToggle, collapsed, onToggleCollapse }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();
    const [expandedItems, setExpandedItems] = useState({
        registry: true,
        userManagement: false
    });
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuToggle = (menuKey) => {
        setExpandedItems(prev => ({
            ...prev,
            [menuKey]: !prev[menuKey]
        }));
    };

    const handleNavigation = (path) => {
        navigate(path);
        if (onMobileToggle) onMobileToggle();
    };

    const isActive = (path) => {
        if (path === '/dashboard' && (location.pathname === '/' || location.pathname === '/dashboard')) {
            return true;
        }
        return location.pathname === path;
    };

    const handleUserMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleUserMenuClose();
        logout();
    };

    const sidebarContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box 
                sx={{ 
                    p: collapsed ? 2 : 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    textAlign: collapsed ? 'center' : 'center',
                    position: 'relative'
                }}
            >
                {!collapsed && (
                    <>
                        <img src={UnikLog} className='unik-log-1' />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                            UNIK Admin
                        </Typography>
                        {/* <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            University Management System
                        </Typography> */}
                    </>
                )}
                {collapsed && (
                    <>
                        <img src={UnikLog} className='unik-log-1' />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            UNIK
                        </Typography>
                    </>
                )}
                
                {/* Collapse/Expand button */}
                {/* <IconButton
                    onClick={onToggleCollapse}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'white',
                        display: { xs: 'none', md: 'flex' }
                    }}
                >
                    {collapsed ? <ChevronRight /> : <ChevronLeft />}
                </IconButton> */}
            </Box>

            {/* Navigation */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <List sx={{ pt: 2 }}>
                    {/* Dashboard */}
                    <ListItem 
                        button 
                        onClick={() => handleNavigation('/dashboard')}
                        sx={{
                            backgroundColor: isActive('/dashboard') ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                            borderRight: isActive('/dashboard') ? '3px solid #667eea' : 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                            },
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            px: collapsed ? 0 : 2
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: collapsed ? 'auto' : '56px' }}>
                            <DashboardIcon color={isActive('/dashboard') ? 'primary' : 'inherit'} />
                        </ListItemIcon>
                        {!collapsed && (
                            <ListItemText 
                                primary="Dashboard" 
                                primaryTypographyProps={{
                                    fontWeight: isActive('/dashboard') ? 'bold' : 'normal',
                                    color: isActive('/dashboard') ? '#667eea' : 'inherit'
                                }}
                            />
                        )}
                    </ListItem>

                    {!collapsed && <Divider sx={{ my: 1 }} />}

                    {/* Registry Section */}
                    {!collapsed ? (
                        <>
                            <ListItem button onClick={() => handleMenuToggle('registry')}>
                                <ListItemIcon>
                                    <FormsIcon />
                                </ListItemIcon>
                                <ListItemText primary="Registry" />
                                {expandedItems.registry ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            
                            <Collapse in={expandedItems.registry} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive('/forms') ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                                            borderRight: isActive('/forms') ? '3px solid #667eea' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                                            }
                                        }}
                                        onClick={() => handleNavigation('/forms')}
                                    >
                                        <ListItemIcon>
                                            <FormsIcon fontSize="small" color={isActive('/forms') ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Forms Management" 
                                            primaryTypographyProps={{
                                                fontWeight: isActive('/forms') ? 'bold' : 'normal',
                                                color: isActive('/forms') ? '#667eea' : 'inherit'
                                            }}
                                        />
                                    </ListItem>
                                    
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive('/app-menu') ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                                            borderRight: isActive('/app-menu') ? '3px solid #667eea' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                                            }
                                        }}
                                        onClick={() => handleNavigation('/app-menu')}
                                    >
                                        <ListItemIcon>
                                            <ApplicationIcon fontSize="small" color={isActive('/app-menu') ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Applications" 
                                            primaryTypographyProps={{
                                                fontWeight: isActive('/app-menu') ? 'bold' : 'normal',
                                                color: isActive('/app-menu') ? '#667eea' : 'inherit'
                                            }}
                                        />
                                    </ListItem>
                                    
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive('/programmes') ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                                            borderRight: isActive('/programmes') ? '3px solid #667eea' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                                            }
                                        }}
                                        onClick={() => handleNavigation('/programmes')}
                                    >
                                        <ListItemIcon>
                                            <ProgrammeIcon fontSize="small" color={isActive('/programmes') ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Programmes" 
                                            primaryTypographyProps={{
                                                fontWeight: isActive('/programmes') ? 'bold' : 'normal',
                                                color: isActive('/programmes') ? '#667eea' : 'inherit'
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive('/applicant-notices-management') ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                                            borderRight: isActive('/applicant-notices-management') ? '3px solid #667eea' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                                            }
                                        }}
                                        onClick={() => handleNavigation('/applicant-notices-management')}
                                    >
                                        <ListItemIcon>
                                            <NotificationsIcon fontSize="small" color={isActive('/applicant-notices-management') ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Applicant Notices" 
                                            primaryTypographyProps={{
                                                fontWeight: isActive('/applicant-notices-management') ? 'bold' : 'normal',
                                                color: isActive('/applicant-notices-management') ? '#667eea' : 'inherit'
                                            }}
                                        />
                                    </ListItem>
                                </List>
                            </Collapse>
                        </>
                    ) : (
                        <ListItem 
                            button 
                            onClick={() => handleNavigation('/dashboard')}
                            sx={{
                                justifyContent: 'center',
                                px: 0
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 'auto' }}>
                                <FormsIcon />
                            </ListItemIcon>
                        </ListItem>
                    )}

                    {!collapsed && <Divider sx={{ my: 1 }} />}

                    {/* User Management Section */}
                    {!collapsed ? (
                        <>
                            <ListItem button onClick={() => handleMenuToggle('userManagement')}>
                                <ListItemIcon>
                                    <UserManagementIcon />
                                </ListItemIcon>
                                <ListItemText primary="User Management" />
                                {expandedItems.userManagement ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            
                            <Collapse in={expandedItems.userManagement} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: isActive('/user-management') ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                                            borderRight: isActive('/user-management') ? '3px solid #667eea' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                                            }
                                        }}
                                        onClick={() => handleNavigation('/user-management')}
                                    >
                                        <ListItemIcon>
                                            <DashboardIcon fontSize="small" color={isActive('/user-management') ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Overview" 
                                            primaryTypographyProps={{
                                                fontWeight: isActive('/user-management') ? 'bold' : 'normal',
                                                color: isActive('/user-management') ? '#667eea' : 'inherit'
                                            }}
                                        />
                                    </ListItem>
                                    
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: (isActive('/users-page') || isActive('/users')) ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                                            borderRight: (isActive('/users-page') || isActive('/users')) ? '3px solid #667eea' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                                            }
                                        }}
                                        onClick={() => handleNavigation('/users-page')}
                                    >
                                        <ListItemIcon>
                                            <PersonAddIcon fontSize="small" color={(isActive('/users-page') || isActive('/users')) ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Users" 
                                            primaryTypographyProps={{
                                                fontWeight: (isActive('/users-page') || isActive('/users')) ? 'bold' : 'normal',
                                                color: (isActive('/users-page') || isActive('/users')) ? '#667eea' : 'inherit'
                                            }}
                                        />
                                    </ListItem>
                                    
                                    <ListItem 
                                        button 
                                        sx={{ 
                                            pl: 4,
                                            backgroundColor: (isActive('/groups-page') || isActive('/groups')) ? 'rgba(103, 126, 234, 0.1)' : 'transparent',
                                            borderRight: (isActive('/groups-page') || isActive('/groups')) ? '3px solid #667eea' : 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 126, 234, 0.05)'
                                            }
                                        }}
                                        onClick={() => handleNavigation('/groups-page')}
                                    >
                                        <ListItemIcon>
                                            <GroupIcon fontSize="small" color={(isActive('/groups-page') || isActive('/groups')) ? 'primary' : 'inherit'} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary="Groups" 
                                            primaryTypographyProps={{
                                                fontWeight: (isActive('/groups-page') || isActive('/groups')) ? 'bold' : 'normal',
                                                color: (isActive('/groups-page') || isActive('/groups')) ? '#667eea' : 'inherit'
                                            }}
                                        />
                                    </ListItem>
                                </List>
                            </Collapse>
                        </>
                    ) : (
                        <ListItem 
                            button 
                            onClick={() => handleNavigation('/user-management')}
                            sx={{
                                justifyContent: 'center',
                                px: 0
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 'auto' }}>
                                <UserManagementIcon />
                            </ListItemIcon>
                        </ListItem>
                    )}
                </List>
            </Box>

            {/* User Profile Section */}
            {!collapsed && (
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                        onClick={handleUserMenuOpen}
                    >
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#667eea', mr: 2 }}>
                            <AccountCircle />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                User Profile
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Click for options
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleUserMenuClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={handleUserMenuClose}>
                            <AccountCircle sx={{ mr: 2 }} />
                            Profile Settings
                        </MenuItem>
                        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                            <Logout sx={{ mr: 2 }} />
                            Logout
                        </MenuItem>
                    </Menu>
                </Box>
            )}
        </Box>
    );

    return (
        <>
            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onMobileToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { 
                        boxSizing: 'border-box', 
                        width: drawerWidth,
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                    },
                }}
            >
                {sidebarContent}
            </Drawer>
            
            {/* Desktop Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': { 
                        boxSizing: 'border-box', 
                        width: collapsed ? collapsedWidth : drawerWidth,
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                        border: 'none',
                        overflowX: 'hidden',
                        transition: (theme) => theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    },
                }}
                open
            >
                {sidebarContent}
            </Drawer>
        </>
    );
};

export default Sidebar;